import elementTypes from "../../Elements/elementTypes"

export const dateAddFunctionName = "addDays"
export const dateSubtractFunctionName = "subtractDays"

export const formulaBuilderTypes = {
  number: "number",
  date: "date",
  time: "time",
  notEligible: undefined,
}

export function getFormulaFunctionList() {
  const functionList = [
    {
      name: "sum",
      display: "Sum()",
      tooltip: "Add a set of numbers together",
      example: "Sum(10, 20, 30) = 60",
    },
    {
      name: "abs",
      display: "Abs()",
      tooltip: "Returns the absolute (positive) value of a number.",
      example: "Abs(-100) = 100",
      maxValues: 1, // Only one value can be added to the function
    },
    {
      name: "log",
      display: "Log()",
      tooltip: "Returns the natural logarithm of a number.",
      example: "Log(2) = 0.69",
    },
    {
      name: "max",
      display: "Max()",
      tooltip: "Returns the highest value from a set of numbers.",
      example: "Max(4, 8, 2) = 8",
    },
    {
      name: "min",
      display: "Min()",
      tooltip: "Returns the lowest value from a set of numbers.",
      example: "Min(1, 2, 5) = 1",
    },
    {
      name: "round",
      display: "Round()",
      tooltip: "Rounds a number to the nearest integer.",
      example: "Round(2.5) = 3",
      hasOffset: true,
    },
    {
      name: "avg",
      display: "Avg()",
      tooltip: "Returns the average of supplied numbers.",
      example: "avg(0, 2, 4) = 2",
    },
    {
      name: "avgNoZero",
      display: "AvgNoZero()",
      tooltip:
        "Returns the average of supplied numbers while excluding zeroes and empty fields.",
      example: "avgNoZero(0, 2, 4) = 3",
    },
    {
      name: "count",
      display: "Count()",
      tooltip:
        "Returns the number of selected options in a Multiple Choice or Single Choice element.",
      example: "Count(0, 2, 4) = 3",
    },
    {
      name: "nth",
      display: "Nth()",
      tooltip: "Returns the nth largest value from a set of numbers.",
      example: "nth(2, 20, 30, 40) = 30",
      hasOffset: true,
    },
    {
      name: dateAddFunctionName,
      display: "AddDays()",
      tooltip: "Adds days to a Date ",
      example: "AddDays(03/23/2024, 2) = 03/25/2024",
      hasOffset: true,
      acceptDateTime: true,
      maxValues: 1, // Only one date can be added to the function
    },
    {
      name: dateSubtractFunctionName,
      display: "SubtractDays()",
      tooltip: "Subtracts days from a Date",
      example: "SubtractDays(03/23/2024, 2) = 03/21/2024",
      hasOffset: true,
      acceptDateTime: true,
      maxValues: 1, // Only one date can be added to the function
    },
    {
      name: "minutes",
      display: "Minutes()",
      tooltip: "Converts minutes to milliseconds.",
      example: "minutes(1) = 60,000",
      acceptDateTime: true,
      maxValues: 1, // Only one date can be added to the function
    },
    {
      name: "hours",
      display: "Hours()",
      tooltip: "Converts hours to milliseconds.",
      example: "hours(1) = 3,600,000",
      acceptDateTime: true,
      maxValues: 1, // Only one date can be added to the function
    },
    {
      name: "days",
      display: "Days()",
      tooltip: "Converts days to milliseconds.",
      example: "days(1) = 86,400,000",
      acceptDateTime: true,
      maxValues: 1, // Only one date can be added to the function
    },
    {
      name: "weeks",
      display: "Weeks()",
      tooltip: "Converts weeks to milliseconds.",
      example: "weeks(1) = 604,800,000",
      acceptDateTime: true,
      maxValues: 1, // Only one date can be added to the function
    },
    {
      name: "months",
      display: "Months()",
      tooltip: "Converts months to milliseconds.",
      example: "months(1) = 2,592,000,000",
      acceptDateTime: true,
      maxValues: 1, // Only one date can be added to the function
    },
    {
      name: "dateDiffMinutes",
      display: "DateDiffMinutes()",
      tooltip:
        "Calculates the difference between 2 dates and returns the number of total minutes difference",
      example: "",
      acceptDateTime: true,
      maxValues: 2, // Only two date can be added to the function
    },
    {
      name: "dateDiffHours",
      display: "DateDiffHours()",
      tooltip:
        "Calculates the difference between 2 dates and returns the number of total hours difference",
      example: "",
      acceptDateTime: true,
      maxValues: 2, // Only two date can be added to the function
    },
    {
      name: "dateDiffDays",
      display: "DateDiffDays()",
      tooltip:
        "Calculates the difference between 2 dates and returns the number of total days difference",
      example: "",
      acceptDateTime: true,
      maxValues: 2, // Only two date can be added to the function
    },
    {
      name: "dateDiffWeeks",
      display: "DateDiffWeeks()",
      tooltip:
        "Calculates the difference between 2 dates and returns the number of total weeks difference",
      example: "",
      acceptDateTime: true,
      maxValues: 2, // Only two date can be added to the function
    },
    {
      name: "dateDiffMonths",
      display: "DateDiffMonths()",
      tooltip:
        "Calculates the difference between 2 dates and returns the number of total months difference",
      example: "",
      acceptDateTime: true,
      maxValues: 2, // Only two date can be added to the function
    },
  ]

  return functionList
}

export function getFunctionByName(name) {
  const functionList = getFormulaFunctionList()
  return functionList.find((func) => func.name === name)
}

export function functionTypeAcceptsQuestionType(
  functionTypeName,
  questionType
) {
  const functionProfile = getFunctionByName(functionTypeName) // Verify function exists
  const questionTypeProfile = elementTypes.findTypeProfile(questionType)

  if (questionTypeProfile && functionProfile) {
    if (
      functionProfile &&
      functionProfile.acceptDateTime &&
      questionTypeProfile?.isDateType
    ) {
      return true
    } else if (
      functionProfile &&
      !functionProfile.acceptDateTime &&
      !questionTypeProfile?.isDateType
    ) {
      return true
    }
  }

  return false
}

export function functionAcceptsMoreValues(draggedOverFunction, draggingValue) {
  const functionProfile = getFunctionByName(draggedOverFunction?.function?.type) // Verify function exists

  if (functionProfile) {
    if (functionProfile.maxValues !== undefined) {
      if (
        draggedOverFunction?.function?.values?.some((valueEntry) => {
          if (valueEntry.isDeleted) {
            return false
          } else {
            return (
              (valueEntry?.question?.id &&
                draggingValue?.question?.id &&
                valueEntry?.question?.id === draggingValue?.question?.id) ||
              (valueEntry?.value?.id &&
                draggingValue?.value?.id &&
                valueEntry?.value?.id === draggingValue?.value?.id)
            )
          }
        })
      ) {
        return true // Already part of the function, so it can be re-added
      } else {
        return (
          draggedOverFunction?.function?.values?.filter(
            (value) => !value.isDeleted
          ).length < functionProfile.maxValues
        )
      }
    } else {
      return true // No max values defined, so it accepts more
    }
  }

  return false // Function not found
}

export function findFormulaOrder(formulaOrderId, formulasList) {
  let result = null

  formulasList.some((formulaOrder) => {
    //Check the top level formula order
    if (formulaOrder.id === formulaOrderId) {
      result = formulaOrder
      return true
    } else if (formulaOrder.group) {
      //Check the group formulas
      const foundInGroup = findFormulaOrder(
        formulaOrderId,
        formulaOrder.group.formulas
      )
      if (foundInGroup) {
        result = foundInGroup
        return true
      }
    } else if (formulaOrder.function) {
      //Check the function values
      const foundInFunction = findFormulaOrder(
        formulaOrderId,
        formulaOrder.function.values
      )
      if (foundInFunction) {
        result = foundInFunction
        return true
      }
    }
    return false
  })

  return result
}
